import React, { useEffect, useRef } from "react";
import { useLongPress } from "react-use";
import { Slider as BaseSlider } from "@base-ui/react"; // Correct package import for v1.2.0

import { IconButton } from "../icon-button";

import "./slider.css";

type Direction = "minus" | "plus";

const convertUiValueToSliderValue = (
	uiValue: number,
	zeroRangeOffset: number,
	_min: number,
	_max: number,
	step: number,
) => {
	if (uiValue > zeroRangeOffset * step) return uiValue - zeroRangeOffset * step;
	if (uiValue < -zeroRangeOffset * step) return uiValue + zeroRangeOffset * step;
	return 0;
};

const convertSliderValueToUiValue = (value: number, zeroRangeOffset: number, step: number) => {
	if (value > 0) return value + zeroRangeOffset * step;
	if (value < 0) return value - zeroRangeOffset * step;
	return 0;
};

function SliderButton({
	value,
	min,
	max,
	step,
	direction,
	disabled,
	onChange,
}: {
	value: number;
	min: number;
	max: number;
	step: number;
	direction: Direction;
	disabled: boolean;
	onChange: (value: number, logGaEvent?: boolean) => void;
}) {
	const changeTimer = useRef<eXpress.IDisposable | undefined>(undefined);

	function clearTimers() {
		if (changeTimer.current) {
			eXpress.utils.dispose(changeTimer.current);
			changeTimer.current = undefined;
		}
	}

	function changeValue(newValue: number) {
		onChange(newValue);
		return newValue >= min && newValue <= max;
	}

	function onLongPress() {
		const initialVelocity = step * 10;
		const acceleration = step * 100;
		clearTimers();

		changeTimer.current = eXpress.platform.requestAnimationFrame.requestAnimationLoop(
			(totalTime) => {
				const sign = direction === "plus" ? 1 : -1;
				const velocity = initialVelocity + 0.5 * acceleration * totalTime;
				const newValue = eXpress.math.quant(value + sign * velocity * totalTime, 1 / step);
				return changeValue(newValue);
			},
		);
	}

	const longPressEvent = useLongPress(onLongPress, {
		isPreventDefault: false,
	});
	const { onMouseUp, onMouseDown, onTouchStart, onTouchEnd } = longPressEvent;

	useEffect(() => clearTimers, []);

	return (
		<IconButton
			onClick={() => {
				let newValue = value;
				if (direction === "plus") newValue += step;
				else if (direction === "minus") newValue -= step;
				if (newValue !== value) changeValue(newValue);
			}}
			onMouseDown={onMouseDown}
			onTouchStart={onTouchStart}
			onMouseUp={() => {
				onMouseUp();
				clearTimers();
			}}
			onTouchEnd={() => {
				onTouchEnd();
				clearTimers();
			}}
			disabled={disabled}
			iconName={direction}
		/>
	);
}

export function Slider({
	value,
	min,
	max,
	step = 1,
	onChange,
	disabled,
	snapToZeroValue = 0,
}: {
	value: number;
	min: number;
	max: number;
	step?: number;
	onChange: (value: number, logGaEvent?: boolean) => void;
	disabled?: boolean;
	snapToZeroValue?: number;
}) {
	const canZoomIn = !disabled && value < max;
	const canZoomOut = !disabled && value > min;
	const canZoom = !disabled && (canZoomIn || canZoomOut);

	const sliderMinUi = min - snapToZeroValue * step;
	const sliderMaxUi = max + snapToZeroValue * step;
	const totalRange = max - min + snapToZeroValue * step * 2;
	const sliderUiValue = convertSliderValueToUiValue(value, snapToZeroValue, step);

	const sliderUiValuePercentage =
		((sliderUiValue - min + snapToZeroValue * step) * 100) / totalRange;

	const thumbSize = 20;
	const isCenteredSlider = min < 0 && max > 0 && Math.abs(min) === max;
	const isLowerHalf = value < 0 && isCenteredSlider;

	const sliderBackground = isCenteredSlider
		? `linear-gradient(to right, 
                var(--surface-color-dark) 0%, 
                var(--surface-color-dark) ${isLowerHalf ? sliderUiValuePercentage : 50}%, 
                var(--primary-color) ${isLowerHalf ? sliderUiValuePercentage : 50}%, 
                var(--primary-color) ${isLowerHalf ? 50 : sliderUiValuePercentage}%, 
                var(--surface-color-dark) ${isLowerHalf ? 50 : sliderUiValuePercentage}%,
                var(--surface-color-dark) 100%)`
		: `linear-gradient(to right, var(--primary-color) 0%, var(--primary-color) ${sliderUiValuePercentage}%, var(--surface-color-dark) ${sliderUiValuePercentage}%, var(--surface-color-dark) 100%)`;

	return (
		<div className="slider">
			<div className="slider__inputs">
				<SliderButton
					value={value}
					min={sliderMinUi}
					max={sliderMaxUi}
					step={step}
					onChange={onChange}
					disabled={disabled || value <= min}
					direction="minus"
				/>
				<div className="slider__input__container">
					<div
						className="slider__value"
						style={{
							left: `calc(${sliderUiValuePercentage}% + (${
								thumbSize / 2 - sliderUiValuePercentage * (thumbSize / 100)
							}px))`,
						}}
					>
						<div className="slider__text">{value}</div>
					</div>

					{/* Base UI v1.2.0 Compound Architecture */}
					<BaseSlider.Root
						className="slider__root"
						min={sliderMinUi}
						max={sliderMaxUi}
						step={step}
						value={sliderUiValue}
						disabled={!canZoom}
						onValueChange={(uiValue) => {
							// Extract single numeric value since uiValue type definition can be number | number[]
							const actualUiValue = Array.isArray(uiValue) ? uiValue[0] : uiValue;
							const newValue = convertUiValueToSliderValue(
								actualUiValue,
								snapToZeroValue,
								min,
								max,
								step,
							);
							onChange(newValue, false);
						}}
					>
						{/* Control wrapper handles slider interaction area */}
						<BaseSlider.Control className="slider__control">
							<BaseSlider.Track
								className="slider__track"
								style={{ background: sliderBackground }}
							>
								<BaseSlider.Thumb className="slider__thumb" />
							</BaseSlider.Track>
						</BaseSlider.Control>
					</BaseSlider.Root>
				</div>
				<SliderButton
					value={value}
					min={sliderMinUi}
					max={sliderMaxUi}
					step={step}
					onChange={onChange}
					disabled={disabled || value >= max}
					direction="plus"
				/>
			</div>
		</div>
	);
}
